<template>
   <div>
      <v-card class="resolution-card">
         <v-toolbar flat color="white">
            <v-toolbar-title>Resolutions</v-toolbar-title>
            <v-divider class="mx-4" inset vertical></v-divider>
            <v-spacer></v-spacer>
            <v-btn color="primary" dark class="mb-2" @click="openAdd">New Item</v-btn>
         </v-toolbar>

         <v-data-table
            :headers="headers"
            :items="listitems"
            :loading="loading"
            sort-by="year"
            class="border resolution-table"
            no-data-text="No data available"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
         >
            <template v-slot:[`item.no`]="{ index }">
               {{ index + 1 }}
            </template>
            <template v-slot:[`item.document`]="{ item }">
               <a v-if="documentUrl(item)" :href="documentUrl(item)" target="_blank">{{ item.document }}</a>
               <span v-else>-</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
               <v-icon small class="mr-3" @click="openEdit(item)">mdi-pencil</v-icon>
               <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
            </template>
         </v-data-table>
      </v-card>

      <v-dialog v-model="dialog" width="800" persistent>
         <v-card>
            <v-card-title>{{ dialogMode === 'add' ? 'New Resolution' : 'Edit Resolution' }}</v-card-title>
            <v-card-text>
               <v-form ref="form" v-model="isvalid">
                  <v-text-field
                     v-model="form.year"
                     label="Year"
                     :rules="[rules.required]"
                     required
                  ></v-text-field>
                  <v-text-field
                     v-model="form.title"
                     label="Title"
                     :rules="[rules.required]"
                     required
                  ></v-text-field>

                  <div class="description-label">Description</div>
                  <div class="editor-shell">
                     <div class="editor-toolbar">
                        <v-icon small>mdi-format-quote-close</v-icon>
                        <v-icon small>mdi-link-variant</v-icon>
                        <v-icon small>mdi-format-underline</v-icon>
                        <v-icon small>mdi-format-strikethrough</v-icon>
                        <v-icon small>mdi-format-italic</v-icon>
                        <v-icon small>mdi-format-list-bulleted</v-icon>
                        <v-icon small>mdi-format-list-numbered</v-icon>
                        <span>H1</span>
                        <span>H2</span>
                        <span>H3</span>
                        <v-icon small>mdi-format-bold</v-icon>
                     </div>
                     <v-textarea
                        v-model="form.description"
                        solo
                        flat
                        hide-details
                        rows="4"
                        class="description-input"
                     ></v-textarea>
                  </div>

                  <v-file-input
                     v-model="form.thumbnail"
                     label="Thumbnail Image"
                     prepend-icon="mdi-paperclip"
                     show-size
                     accept="image/*"
                     :placeholder="existingThumbnail"
                  ></v-file-input>
                  <v-file-input
                     v-model="form.document"
                     label="Attached Document"
                     prepend-icon="mdi-paperclip"
                     show-size
                     accept=".pdf,.doc,.docx"
                     :rules="documentRules"
                     :placeholder="existingDocument"
                  ></v-file-input>
               </v-form>
            </v-card-text>
            <v-card-actions class="pb-3 pr-4">
               <v-spacer></v-spacer>
               <v-btn color="primary" dark :loading="saving" @click="saveItem">
                  {{ dialogMode === 'add' ? 'Save' : 'Update' }}
               </v-btn>
               <v-btn color="red darken-1" dark @click="closeDialog">Close</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <v-dialog v-model="dlgconfirm" width="420" persistent>
         <v-card>
            <v-card-title>Delete Resolution</v-card-title>
            <v-card-text>
               Confirm delete <strong>{{ todelete ? todelete.title : '' }}</strong>?
            </v-card-text>
            <v-card-actions>
               <v-spacer></v-spacer>
               <v-btn color="red darken-1" dark :loading="saving" @click="confirmDelete">Delete</v-btn>
               <v-btn text @click="closeConfirm">Cancel</v-btn>
            </v-card-actions>
         </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar" :color="snackbarColor">{{ snackbarText }}</v-snackbar>
   </div>
</template>

<script>
const axios = require('axios')

export default {
   name: 'TableResolutions',
   data: () => ({
      loading: false,
      saving: false,
      isvalid: false,
      dialog: false,
      dialogMode: 'add',
      dlgconfirm: false,
      todelete: null,
      existingDocument: '',
      existingThumbnail: '',
      form: {
         id: null,
         year: '',
         title: '',
         description: '',
         thumbnail: null,
         document: null,
      },
      snackbar: false,
      snackbarColor: 'info',
      snackbarText: '',
      headers: [
         { text: 'No.', align: 'start', sortable: true, width: 70, value: 'no' },
         { text: 'Year', sortable: true, width: 120, value: 'year' },
         { text: 'Title', sortable: false, value: 'title' },
         { text: 'Description', sortable: false, value: 'description' },
         { text: 'Attached Document', sortable: false, value: 'document' },
         { text: 'Actions', value: 'actions', sortable: false, align: 'end', width: 130 },
      ],
      listitems: [],
      rules: {
         required: value => !!value || 'This information is required',
      },
   }),
   computed: {
      documentRules() {
         return this.dialogMode === 'add' ? [this.rules.required] : []
      },
   },
   mounted() {
      this.initialize()
   },
   methods: {
      initialize() {
         this.loading = true
         axios.get('/admin/agm/resolutions')
            .then((resp) => {
               this.listitems = resp.data && resp.data.status === 'OK' ? (resp.data.data || []) : []
            })
            .catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.loading = false
            })
      },
      openAdd() {
         this.dialogMode = 'add'
         this.resetForm()
         this.dialog = true
      },
      openEdit(item) {
         this.dialogMode = 'edit'
         this.form = {
            id: item.id,
            year: item.year || '',
            title: item.title || '',
            description: item.description || '',
            thumbnail: null,
            document: null,
         }
         this.existingDocument = item.document || ''
         this.existingThumbnail = item.thumbnail || ''
         this.dialog = true
      },
      closeDialog() {
         this.dialog = false
      },
      resetForm() {
         this.form = {
            id: null,
            year: '',
            title: '',
            description: '',
            thumbnail: null,
            document: null,
         }
         this.existingDocument = ''
         this.existingThumbnail = ''
         this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.resetValidation()
         })
      },
      saveItem() {
         if (!this.$refs.form.validate()) return

         this.saving = true
         const data = new FormData()
         if (this.dialogMode === 'edit') data.append('id', this.form.id)
         data.append('year', this.form.year)
         data.append('title', this.form.title)
         data.append('description', this.form.description || '')
         if (this.form.thumbnail) data.append('thumbnail', this.form.thumbnail)
         if (this.form.document) data.append('document', this.form.document)

         const url = this.dialogMode === 'add' ? '/admin/agm/resolution/add' : '/admin/agm/resolution/update'
         axios.post(url, data)
            .then((resp) => {
               const ok = resp.data && resp.data.status === 'OK'
               this.notify(ok, ok ? (resp.data.message || 'Record saved') : (resp.data.error || 'Unable to save record.'))
               if (ok) {
                  this.dialog = false
                  this.initialize()
               }
            })
            .catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.saving = false
            })
      },
      deleteItem(item) {
         this.todelete = item
         this.dlgconfirm = true
      },
      closeConfirm() {
         this.todelete = null
         this.dlgconfirm = false
      },
      confirmDelete() {
         if (!this.todelete) return
         this.saving = true
         const data = new FormData()
         data.append('id', this.todelete.id)
         axios.post('/admin/agm/resolution/delete', data)
            .then((resp) => {
               const ok = resp.data && resp.data.status === 'OK'
               this.notify(ok, ok ? 'Record deleted' : (resp.data.error || 'Unable to delete record.'))
               this.closeConfirm()
               this.initialize()
            })
            .catch((error) => this.notify(false, this.errorText(error)))
            .finally(() => {
               this.saving = false
            })
      },
      documentUrl(item) {
         if (!item.document || !item.documentpath) return null
         return '/' + String(item.documentpath).replace(/^\/+|\/+$/g, '') + '/' + item.document
      },
      notify(ok, text) {
         this.snackbarColor = ok ? 'success' : 'error'
         this.snackbarText = text
         this.snackbar = true
      },
      errorText(error) {
         return error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Unable to complete request.'
      },
   },
}
</script>

<style scoped>
.resolution-card ::v-deep .v-toolbar__title {
   font-size: 18px;
   font-weight: 500;
}

.resolution-table ::v-deep .v-data-table-header th {
   font-size: 12px;
   font-weight: 600;
   color: #263238 !important;
}

.resolution-table ::v-deep tbody td {
   font-size: 12px;
   line-height: 1.35;
}

.description-label {
   margin-top: 18px;
   margin-bottom: 4px;
   color: rgba(0, 0, 0, 0.6);
   font-size: 14px;
}

.editor-shell {
   border: 1px solid #e5e7eb;
   margin-bottom: 18px;
}

.editor-toolbar {
   display: flex;
   align-items: center;
   gap: 14px;
   height: 36px;
   padding: 0 14px;
   background: #f5f5f5;
   color: #616161;
   font-size: 11px;
   font-weight: 700;
}

.description-input ::v-deep textarea {
   font-size: 14px;
   line-height: 1.5;
}
</style>
